import React, { useState, useEffect } from 'react';
import { Typography, Box, Select, MenuItem } from '@mui/material';
import { GroundStationManager } from 'models';

import { useAppDispatch, useAppSelector } from 'hooks';
import { sendGroundStationCommand } from 'state/groundstations';
import { receiveMessage } from 'state/websockets';

import RefreshButton from 'components/RefreshButton';

const DEFAULT_NUMBER_OF_LOGS = 25;

const DEFAULT_UPDATE_TIME = 300_000; // 5 Minutes

interface Props {
    groundstation: GroundStationManager.GroundStation;
}

const DisplayLogs: React.FunctionComponent<Props> = ({ groundstation }) => {
    const dispatch = useAppDispatch();

    const { latestCommands } = useAppSelector((root) => root.groundstations);

    const [numberOfLogs, setNumberOfLogs] = useState(DEFAULT_NUMBER_OF_LOGS);

    const [logs, setLogs] = useState<string[]>();

    const [sentCommand, setSentCommand] =
        useState<GroundStationManager.API.Command>();

    const [sending, setSending] = useState(false);

    const getLogs = async () => {
        if (!groundstation.connectionId) return;

        setSending(true);

        try {
            const results = await dispatch(
                sendGroundStationCommand({
                    id: groundstation.id,
                    command: 'get_logs',
                    params: {
                        // Date not supported for now
                        // date: undefined,
                        rows: numberOfLogs,
                    },
                }),
            );

            if (results.error)
                dispatch(
                    receiveMessage({
                        command: 'message',
                        is_ground_station: true,
                        from_id: groundstation.id,
                        from: groundstation.name,
                        message:
                            results.error ??
                            'No Error Message from Ground Station',
                        status: 'error',
                    }),
                );

            if (results.command) setSentCommand(results.command);
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        const latestSentCommand =
            sentCommand != null ? latestCommands[sentCommand.id] : null;

        if (latestSentCommand && groundstation) {
            if (latestSentCommand.status === 'FINISHED') {
                setLogs((latestSentCommand.results?.logs ?? []) as string[]);
                setSentCommand(undefined);
            } else if (latestSentCommand.status === 'ERROR') {
                dispatch(
                    receiveMessage({
                        command: 'message',
                        is_ground_station: true,
                        from_id: groundstation.id,
                        from: groundstation.name,
                        message:
                            latestSentCommand.error ??
                            'No Error Message from Ground Station',
                        status: 'error',
                    }),
                );
            }
        }
    }, [latestCommands]);

    useEffect(() => {
        const updaterFn = setInterval(getLogs, DEFAULT_UPDATE_TIME);

        getLogs();

        return () => {
            clearInterval(updaterFn);
        };
    }, [numberOfLogs]);

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateRows: 'auto 1fr',
                gap: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Box>
                    <Typography variant="h6">Ground Station Logs</Typography>
                    <Typography variant="subtitle1">
                        Displays the most recent logs.
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                    }}
                >
                    <Select
                        value={numberOfLogs}
                        onChange={(event) => {
                            const newValue = Number.parseFloat(
                                `${event.target.value}`,
                            );
                            if (!Number.isNaN(newValue))
                                setNumberOfLogs(newValue);
                        }}
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={250}>250</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                        <MenuItem value={1000}>1000</MenuItem>
                    </Select>
                    <RefreshButton
                        isRefreshing={sending}
                        onClick={() => getLogs()}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gap: 1,
                }}
            >
                {(!logs || logs.length == 0) && (
                    <Typography variant="subtitle2">
                        No Logs Available
                    </Typography>
                )}
                {!groundstation.connectionId && (
                    <Typography variant="subtitle2">
                        Ground Station is not Connected
                    </Typography>
                )}
                {logs && logs.length > 0 && (
                    <pre style={{ whiteSpace: 'pre-wrap' }}>
                        {logs?.join('\n')}
                    </pre>
                )}
            </Box>
        </Box>
    );
};

DisplayLogs.displayName = 'DisplayLogs';
export default DisplayLogs;
